import CheckOutlinedIcon from "@mui/icons-material/CheckOutlined";
import ClearOutlinedIcon from "@mui/icons-material/ClearOutlined";
import { Button, Tooltip } from "@mui/material";
import { useSetIdentityVerificationStatusMutation } from "Api/Mutations/User/useSetIdentityVerificationStatusMutation";
import { IdentityVerificationStatus } from "Infrastructure/Api/Api";
import { toast } from "sonner";
import { Resources, useResource } from "Translations/Resources";

const PAGE_RESOURCES = Resources.Pages.Content.UserDetailPageContent.Tabs.Party;

type Props = {
  userID: number;
  currentStatus: IdentityVerificationStatus;
  isDisabled?: boolean;
  disabledTooltip?: string;
};

export const IdentityVerificationButton = ({
  userID,
  currentStatus,
  isDisabled,
  disabledTooltip,
}: Props) => {
  const { t } = useResource();

  const { mutate: setVerificationStatus, isPending } =
    useSetIdentityVerificationStatusMutation({
      onError: () => {
        toast.error(t(Resources.Pages.Content.UserDetailPageContent.SaveError));
      },
    });

  const isVerified = currentStatus === IdentityVerificationStatus.Verified;
  const isProcessing = currentStatus === IdentityVerificationStatus.Processing;

  const handleCancelVerification = () => {
    setVerificationStatus(
      {
        targetUserID: userID,
        identityVerificationStatus: IdentityVerificationStatus.NotVerified,
      },
      {
        onSuccess: () => toast.success(t(PAGE_RESOURCES.IdentityNotVerified)),
      },
    );
  };

  const handleSetVerified = () => {
    setVerificationStatus(
      {
        targetUserID: userID,
        identityVerificationStatus: IdentityVerificationStatus.Verified,
      },
      {
        onSuccess: () => toast.success(t(PAGE_RESOURCES.IdentityVerified)),
      },
    );
  };

  if (isVerified) {
    return (
      <Button
        variant="outlined"
        size="small"
        color="error"
        disabled={isPending}
        startIcon={<ClearOutlinedIcon />}
        onClick={handleCancelVerification}
      >
        {t(PAGE_RESOURCES.CancelVerification)}
      </Button>
    );
  }

  const isButtonDisabled = isPending || isProcessing || isDisabled;

  const button = (
    <Button
      variant="outlined"
      size="small"
      color={isButtonDisabled ? "inherit" : "primary"}
      disabled={isButtonDisabled}
      startIcon={<CheckOutlinedIcon />}
      onClick={handleSetVerified}
      sx={{
        "&.Mui-disabled": {
          borderColor: "action.disabled",
        },
      }}
    >
      {t(PAGE_RESOURCES.SetVerified)}
    </Button>
  );

  if (isDisabled && disabledTooltip) {
    return (
      <Tooltip title={disabledTooltip}>
        <span>{button}</span>
      </Tooltip>
    );
  }

  return button;
};
